import dayjs from 'dayjs';
import { jsPDF } from 'jspdf';

const PAGE_MARGIN = 36;
const RENDER_TIMEOUT_MS = 30000;
const SETTLE_MS = 500;
const MAX_CAPTURE_WIDTH = 1600;

export class SiteMapPrintError extends Error {}

export interface SiteMapPage {
  mapDataUrl: string;
  mapSize: { width: number; height: number };
  mapName: string;
}

/** Pixel size the offscreen map is mounted at, scaled down from the source image. */
export const getCaptureSize = ({ width, height }: { width: number; height: number }) => {
  const scale = Math.min(1, MAX_CAPTURE_WIDTH / width);

  return { width: Math.round(width * scale), height: Math.round(height * scale) };
};

/** Natural size of a map image, needed before it can be mounted for capture. */
export const loadImageDimensions = (src: string) =>
  new Promise<{ width: number; height: number }>((resolve, reject) => {
    const image = new Image();

    image.onload = () => resolve({ width: image.naturalWidth, height: image.naturalHeight });
    image.onerror = () => reject(new SiteMapPrintError('The site map image could not be read.'));
    image.src = src;
  });

/** Viewport rect the map image occupies, read off the view so it matches what OpenLayers drew. */
const getImageRect = (map: any) => {
  const [, , width, height] = map.getView().getProjection().getExtent();
  const topLeft = map.getPixelFromCoordinate([0, height]);
  const bottomRight = map.getPixelFromCoordinate([width, 0]);

  return {
    x: topLeft[0],
    y: topLeft[1],
    width: bottomRight[0] - topLeft[0],
    height: bottomRight[1] - topLeft[1],
  };
};

/** Flattens the base image and every OpenLayers layer canvas into one bitmap. */
export const captureMapImage = (map: any, mapImgSrc: string): Promise<string> =>
  new Promise((resolve, reject) => {
    const timeout = setTimeout(
      () => reject(new SiteMapPrintError('The site map took too long to render on this device.')),
      RENDER_TIMEOUT_MS,
    );

    map.once('rendercomplete', () => {
      clearTimeout(timeout);

      const baseImage = new Image();

      // The static image source is not counted by rendercomplete, so the base map is drawn
      // here from the same source, positioned by the view's own transform.
      baseImage.onload = () => {
        setTimeout(() => {
          map.renderSync();
          compose();
        }, SETTLE_MS);
      };
      baseImage.onerror = () => reject(new SiteMapPrintError('The site map image could not be read.'));

      const compose = () => {
        try {
          const [width, height] = map.getSize();
          const ratio = window.devicePixelRatio || 1;
          const canvas = document.createElement('canvas');

          canvas.width = Math.round(width * ratio);
          canvas.height = Math.round(height * ratio);

          const ctx = canvas.getContext('2d');

          if (!ctx) {
            throw new SiteMapPrintError('Canvas is unavailable on this device.');
          }

          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, canvas.width, canvas.height);

          const rect = getImageRect(map);

          ctx.drawImage(
            baseImage,
            rect.x * ratio,
            rect.y * ratio,
            rect.width * ratio,
            rect.height * ratio,
          );

          map.getViewport()
            .querySelectorAll('canvas')
            .forEach((layerCanvas: HTMLCanvasElement) => {
              if (!layerCanvas.width || !layerCanvas.height) {
                return;
              }

              const opacity =
                (layerCanvas.parentNode as HTMLElement)?.style?.opacity || layerCanvas.style.opacity;
              const transform = layerCanvas.style.transform;
              const matrix = transform
                ? transform.match(/^matrix\(([^(]*)\)$/)?.[1].split(',').map(Number)
                : null;

              ctx.globalAlpha = opacity === '' || opacity == null ? 1 : Number(opacity);
              ctx.setTransform(
                ...((matrix || [1, 0, 0, 1, 0, 0]) as [number, number, number, number, number, number]),
              );
              ctx.drawImage(layerCanvas, 0, 0);
            });

          ctx.globalAlpha = 1;
          ctx.setTransform(1, 0, 0, 1, 0, 0);

          resolve(canvas.toDataURL('image/png'));
        } catch (error) {
          reject(error);
        }
      };

      baseImage.src = mapImgSrc;
    });

    map.render();
  });

/** Mirrors the server's siteMapReport heading: project/client and date left, map name right. */
const addHeading = (
  doc: jsPDF,
  pageWidth: number,
  { mapName, projectName, clientName }: { mapName: string; projectName: string; clientName?: string },
) => {
  const title = clientName ? `${projectName} - ${clientName}` : projectName;
  const asOf = `Active Site Map as of  ${dayjs().format('MM-DD-YYYY')}`;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');

  doc.text(title, PAGE_MARGIN, PAGE_MARGIN);
  doc.text(asOf, PAGE_MARGIN, PAGE_MARGIN + 13);
  doc.text(mapName, pageWidth - PAGE_MARGIN, PAGE_MARGIN, { align: 'right' });

  doc.setFont('helvetica', 'normal');
};

export const buildSiteMapPdf = (options: {
  pages: SiteMapPage[];
  projectName: string;
  clientName?: string;
}) => {
  const { pages, projectName, clientName } = options;
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'letter' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  pages.forEach(({ mapDataUrl, mapSize, mapName }, page) => {
    if (page > 0) {
      doc.addPage();
    }

    addHeading(doc, pageWidth, { mapName, projectName, clientName });

    const top = PAGE_MARGIN + 26;
    const maxWidth = pageWidth - PAGE_MARGIN * 2;
    const maxHeight = pageHeight - top - PAGE_MARGIN;
    const scale = Math.min(maxWidth / mapSize.width, maxHeight / mapSize.height);
    const width = mapSize.width * scale;
    const height = mapSize.height * scale;

    doc.addImage(mapDataUrl, 'PNG', PAGE_MARGIN + (maxWidth - width) / 2, top, width, height);
  });

  const name = pages.length === 1 ? `${projectName}_${pages[0].mapName}` : `${projectName}_site_maps`;

  doc.save(`${name}.pdf`);
};
